import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Clock, Play, Square, Plus, Timer, FileText } from "lucide-react";

export default function TimeTracking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [runningEntry, setRunningEntry] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [form, setForm] = useState({
    client_id: "",
    sla_contract_id: "",
    ticket_id: "",
    description: "",
    hours: "",
    entry_type: "manual",
  });

  const fetchData = useCallback(async () => {
    try {
      const [entriesRes, contractsRes] = await Promise.all([
        apiClient.get("/time-entries"),
        apiClient.get("/sla-contracts?active_only=true"),
      ]);
      setEntries(entriesRes.data);
      setContracts(contractsRes.data);

      // Find running entry
      const running = entriesRes.data.find((e) => e.is_running);
      setRunningEntry(running || null);

      // Get unique clients from contracts
      const uniqueClients = {};
      contractsRes.data.forEach((c) => { uniqueClients[c.client_id] = c.client_name; });
      setClients(Object.entries(uniqueClients).map(([id, name]) => ({ id, name })));
    } catch {
      toast.error("Failed to load time data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Timer tick
  useEffect(() => {
    if (!runningEntry) { setElapsed(0); return; }
    const start = new Date(runningEntry.start_time).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [runningEntry]);

  const formatElapsed = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const handleStartTimer = async () => {
    if (!form.client_id) { toast.error("Select a client"); return; }
    try {
      await apiClient.post("/time-entries", {
        client_id: form.client_id,
        sla_contract_id: form.sla_contract_id || null,
        ticket_id: form.ticket_id || null,
        description: form.description || null,
        entry_type: "timer",
      });
      toast.success("Timer started");
      setAddOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start timer");
    }
  };

  const handleStopTimer = async () => {
    if (!runningEntry) return;
    try {
      await apiClient.post(`/time-entries/${runningEntry.id}/stop`);
      toast.success("Timer stopped");
      fetchData();
    } catch {
      toast.error("Failed to stop timer");
    }
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.hours) { toast.error("Client and hours required"); return; }
    try {
      await apiClient.post("/time-entries", {
        client_id: form.client_id,
        sla_contract_id: form.sla_contract_id || null,
        ticket_id: form.ticket_id || null,
        description: form.description || null,
        hours: parseFloat(form.hours),
        entry_type: "manual",
      });
      toast.success("Time entry logged");
      setAddOpen(false);
      setForm({ client_id: "", sla_contract_id: "", ticket_id: "", description: "", hours: "", entry_type: "manual" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log time");
    }
  };

  const clientContracts = contracts.filter((c) => c.client_id === form.client_id);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="time-tracking-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">Time Tracking</h1>
            <p className="text-slate-500 mt-1">Log and manage your billable hours</p>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sag-navy hover:bg-sag-navy-800" data-testid="log-time-button">
                <Plus className="h-4 w-4 mr-2" /> Log Time
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Log Time Entry</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select value={form.client_id || "placeholder"} onValueChange={(v) => setForm({ ...form, client_id: v === "placeholder" ? "" : v, sla_contract_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="Select client..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder" disabled>Select client...</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {clientContracts.length > 0 && (
                  <div className="space-y-2">
                    <Label>SLA Contract</Label>
                    <Select value={form.sla_contract_id || "none"} onValueChange={(v) => setForm({ ...form, sla_contract_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No contract</SelectItem>
                        {clientContracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.title} ({c.hours_used}/{c.total_hours}h)</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea placeholder="What did you work on?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="time-description-input" />
                </div>
                <div className="space-y-2">
                  <Label>Hours (for manual entry)</Label>
                  <Input type="number" step="0.25" min="0.25" placeholder="1.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="time-hours-input" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleManualEntry} className="flex-1 bg-sag-navy hover:bg-sag-navy-800" disabled={!form.client_id || !form.hours} data-testid="submit-manual-time">
                    <FileText className="h-4 w-4 mr-2" /> Log Manual Entry
                  </Button>
                  <Button onClick={handleStartTimer} variant="outline" className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={!form.client_id || !!runningEntry} data-testid="start-timer-button">
                    <Play className="h-4 w-4 mr-2" /> Start Timer
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Active Timer */}
        {runningEntry && (
          <Card className="border-2 border-emerald-300 bg-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-emerald-100 animate-pulse">
                    <Timer className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-lg text-emerald-900">Timer Running</p>
                    <p className="text-sm text-emerald-700">
                      {runningEntry.client_name} {runningEntry.description ? `- ${runningEntry.description}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-3xl font-bold text-emerald-900" data-testid="timer-display">
                    {formatElapsed(elapsed)}
                  </span>
                  <Button onClick={handleStopTimer} variant="destructive" size="lg" data-testid="stop-timer-button">
                    <Square className="h-4 w-4 mr-2" /> Stop
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Entries Table */}
        <Card className="sag-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg">Time Entries</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center">
                <Clock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No time entries yet. Start tracking!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`time-entry-${entry.id}`}>
                      <TableCell className="text-slate-600 text-sm">
                        {new Date(entry.start_time).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">{entry.client_name}</TableCell>
                      <TableCell className="text-slate-600 max-w-[200px] truncate">{entry.description || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={entry.entry_type === "timer" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}>
                          {entry.entry_type === "timer" ? "Timer" : "Manual"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono font-medium">{entry.hours ? `${entry.hours}h` : "-"}</TableCell>
                      <TableCell>
                        {entry.is_running ? (
                          <Badge className="bg-emerald-100 text-emerald-700 animate-pulse" variant="secondary">Running</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700" variant="secondary">Completed</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
